import { ref, Ref } from 'vue';
import globals from '@/utils/globals';
import Actions from '@/dictionary/tracking/actions';
import { StructEvent } from '@/utils/tracking/events/struct';
import { IncentiveAuthenticationInput } from "@/domain/dto/student-authentication-state.interface";
import { IIncentiveV2Store } from "@/domain/dto/store.incentive-v2.interfaces";
import { buildLegacyRegistrationReferrer, trackIncentiveAction } from "@/utils/tracking/incentive-tracking";
import { Referrer } from 'interfaces';

export class SocialIncentiveButtonsBase {
	public root: Ref<HTMLElement | null> = ref(null);
	private mutationObserver: MutationObserver | null = null;
	private intersectionObserver: IntersectionObserver | null = null;
	protected hasButtonRendered = false;
	private wasVisible = false;
	private hasErrorFired = false;

	protected buttonName = 'SocialButton';

	constructor(protected store: IIncentiveV2Store, protected isRegistration: boolean) {}

	protected startObservingDOM(): void {
		if (!this.root.value || this.mutationObserver) return;

		this.mutationObserver = new MutationObserver((mutations) => this.handleDOMChanges(mutations));

		this.mutationObserver.observe(this.root.value, {
			childList: true,
			subtree: true,
			attributes: false,
		});
	}

	private startObservingVisibility(): void {
		if (!this.root.value || this.intersectionObserver) return;

		this.intersectionObserver = new IntersectionObserver(
			(entries) => this.handleVisibilityChanges(entries),
			{ threshold: 0 },
		);

		this.intersectionObserver.observe(this.root.value);
	}

	private handleVisibilityChanges(entries: IntersectionObserverEntry[]): void {
		const entry = entries[0];
		if (!entry) return;

		if (entry.isIntersecting) {
			this.wasVisible = true;
			return;
		}

		if (!this.wasVisible) return;

		if (!this.hasButtonRendered) {
			this.fireError();
		}

		this.cleanupIntersectionObserver();
	}

	private handleDOMChanges(mutations: MutationRecord[]): void {
		const hasAddedNodes = mutations.some((mutation) => mutation.addedNodes.length > 0);

		if (!hasAddedNodes) return;

		this.checkIfButtonIsRendered();
	}

	protected checkIfButtonIsRendered(): void {
		if (!this.root.value || this.root.value.children.length === 0) return;

		this.hasButtonRendered = true;
		this.cleanupObserver();
	}

	protected fireError(): void {
		if (this.hasErrorFired) return;
		this.hasErrorFired = true;

		const referrer = buildLegacyRegistrationReferrer(
			this.store.getters.incentiveType(),
			this.store.getters.location(),
			this.store.getters.uniqueIdentifier(),
		);
		const portalType = this.store.getters.portalType();
		const errorMessage = `${this.buttonName}NotRendered ${referrer}:${portalType}`;

		globals.authenticationServiceTracking.trackError(errorMessage, referrer as unknown as Referrer);
		globals.authenticationServiceTracking.track(
			new StructEvent(
				{
					action: Actions.GENERAL,
					// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
					label: errorMessage,
				},
				referrer as unknown as Referrer,
			),
		);

		this.cleanupObserver();
	}

	protected cleanupObserver(): void {
		if (!this.mutationObserver) return;

		this.mutationObserver.disconnect();
		this.mutationObserver = null;
	}

	private cleanupIntersectionObserver(): void {
		if (!this.intersectionObserver) return;

		this.intersectionObserver.disconnect();
		this.intersectionObserver = null;
	}

	public mounted(): void {
		this.startObservingDOM();
		this.startObservingVisibility();
		this.checkIfButtonIsRendered();
	}

	public unmount(): void {
		if (!this.hasButtonRendered) {
			this.fireError();
		}

		this.cleanupObserver();
		this.cleanupIntersectionObserver();
	}

	protected async signInWithProvider (input: IncentiveAuthenticationInput): Promise<void> {
		try {
			if (this.isRegistration) {
				await this.store.actions.register(input);
			} else {
				await this.store.actions.login(input);
			}
		} catch {
			this.store.mutations.setErrorMessage({
				message: `Social sign-in was not completed. with provider ${input.provider}`,
				type: null,
			});
		}
	}

	protected trackButtonClick(action: string): void {
		try {
			trackIncentiveAction(
				action,
				this.store.getters.incentiveType(),
				this.store.getters.location(),
				this.store.getters.uniqueIdentifier()
			);
		} catch {
			this.store.mutations.setErrorMessage({
				message: `Tracking button click failed for action ${action}`,
				type: null,
			});
		}
	}
}